import { ReactElement } from 'react'

import { InfoCircleOutlined } from '@ant-design/icons'
import { Tag } from 'antd'
import get from 'lodash/get'
import { Trans } from 'react-i18next'
import { generatePath, Link } from 'react-router-dom'

import { Segment } from 'api/Cerebro/orgScoped/segments/types'
import { Flex } from 'components/core'
import { ToolTip } from 'components/ToolTip'
import { UNDEFINED_VALUE, UNKNOWN_VALUE } from 'const/formatting'
import SegmentTypeTag from 'features/Segments/components/SegmentTypeTag/SegmentTypeTag'
import { isUnset } from 'helpers/utilities'
import { getPath } from 'routing/pagePaths'
import { CellRenderProps } from 'types'

type RenderProps<RecordType> = {
    cellRenderProps: CellRenderProps<RecordType>
}

interface SegmentInfo {
    id: string
    seller_name?: string
    segment: Segment
}

export function segmentNameRenderer<RecordType extends Segment>(
    { cellRenderProps: { record } }: RenderProps<RecordType>,
    pathToSegment: string[] = []
): ReactElement {
    const segment =
        !!record && pathToSegment?.length ? get(record, pathToSegment) : record

    if (isUnset(segment)) {
        return <span>{UNDEFINED_VALUE}</span>
    }

    const tooltipMessage = (
        <Trans i18nKey="segments:Tooltip.Table.demoMessage.alreadyDemo">
            ASINs in this segment do not count against your monthly total.
        </Trans>
    )

    const demoTag = segment.is_demo ? (
        <ToolTip title={tooltipMessage}>
            <Tag
                className="fg-default-tag"
                bordered={false}
                style={{
                    margin: 0,
                    padding: '3px 4px',
                    border: 'none',
                    cursor: 'default',
                }}
            >
                Demo
            </Tag>
        </ToolTip>
    ) : null

    const segmentTypeTag = <SegmentTypeTag segment={record} size="small" />

    const showTag = !!(demoTag || segmentTypeTag)

    return (
        <Flex gap={8} style={{ flexWrap: 'wrap' }}>
            <Link
                to={{
                    pathname: generatePath(getPath('segmentPage'), {
                        segmentId: segment.id,
                    }),
                }}
                style={{ textWrap: 'initial' }}
            >
                {segment.name}
            </Link>
            {showTag && (
                <Flex gap={4} style={{ flex: 1 }}>
                    {demoTag}
                    {segmentTypeTag}
                </Flex>
            )}
        </Flex>
    )
}

export function segmentAsinsRenderer<RecordType extends Segment>({
    record,
}: CellRenderProps<RecordType>): ReactElement {
    const tooltipMessage = (
        <Trans i18nKey="segments:Tooltip.Table.demoMessage.alreadyDemo">
            ASINs in this segment do not count against your monthly total.
        </Trans>
    )

    const notCountedMsg = record.is_demo ? (
        <span>
            Not counted{' '}
            <ToolTip title={tooltipMessage}>
                <InfoCircleOutlined />
            </ToolTip>
        </span>
    ) : null

    return (
        <div>
            <div>{record.asin_count ?? UNDEFINED_VALUE}</div>
            <small>{notCountedMsg}</small>
        </div>
    )
}

export function sellerNameRenderer<RecordType extends SegmentInfo>({
    record,
}: CellRenderProps<RecordType>): ReactElement {
    const tooltipMessage = (
        <Trans i18nKey="segments:Tooltip.Table.unknownSeller">
            We weren’t able to identify a seller for this set of ASINs.
            <br />
            This could be due to new sellers whose data we haven’t collected yet
            <br />
            or an inability to assign a buybox owner at the time of sale.
        </Trans>
    )

    if (isUnset(record.seller_name)) {
        return (
            <span>
                {UNKNOWN_VALUE}{' '}
                <ToolTip title={tooltipMessage}>
                    <InfoCircleOutlined />
                </ToolTip>
            </span>
        )
    }

    return <span>{record.seller_name}</span>
}
